import { MentalHealthScreeningData } from "src/pages/MentalHealthScreeningScreen/types/MentalHealthScreeningTypes";
import Disability from "src/shared/utils/enums/Disability.enum";
import EducationalAttainment from "src/shared/utils/enums/EducationalAttainment";
import HousingSituation from "src/shared/utils/enums/HousingSituation";
import IncarcerationDuration from "src/shared/utils/enums/IncarcerationDuration";
import MaritalStatus from "src/shared/utils/enums/MaritalStatus.enum";
import TransportationSituation from "src/shared/utils/enums/TransportationSituation";
import USState from "src/types/states";
import { PublicEligibilityDecision } from "src/types/User";
import { VocationId } from "src/types/Vocation";
import { BackgroundFormData as LegacyBackgroundFormData } from "../legacy/BackgroundSurveyView";
import DriverLicenseInactivityReason from "../legacy/DriverLicenseInactivityReason.enum";
import SocialBenefits from "../legacy/SocialBenefits.enum";

// Union type for backward compatibility
export type BackgroundFormData =
  | LegacyBackgroundFormData // Legacy format (no version field)
  | ApplicationWizardData; // New format (with version: 'v2')

// Type guard for legacy applications
export function isLegacyApplication(
  application: BackgroundFormData
): application is LegacyBackgroundFormData {
  return !("version" in application) || application.version === undefined;
}

// Type guard for v2 applications
export function isV2Application(
  application: BackgroundFormData
): application is ApplicationWizardData {
  return "version" in application && application.version === "v2";
}

export type TradeExperienceAnswer = {
  key: string;
  question: string;
  answer: string;
};

/**
 * Trade-specific application answers for non-CDL vocations (mirrors
 * functions/types/ApplicationV2.ts). Question wording travels with each
 * answer so ops can read a submission even after the vocation doc's
 * questions change.
 */
export type TradeExperience = {
  vocationId: VocationId;
  answers: TradeExperienceAnswer[];
};

// New Application Wizard Data Structure
export type ApplicationWizardData = {
  version: "v2";
  personal: PersonalInformation;
  financial: FinancialInformation;
  driverLicense: DriverLicenseRecord;
  criminalRecord: CriminalRecord;
  medical: MedicalRecord;
  // Added January 2026. Optional to avoid triggering a new application version.
  // In v3, this should be required (not nullable).
  vocational?: VocationalHistory;
  // Vocation-doc-driven trade questions (non-CDL vocations only).
  tradeExperience?: TradeExperience;

  // === Optional Assessment Fields ===
  // These keys must match AssessmentType in Organization.ts
  // When adding a new assessment:
  // 1. Add the type here as optional field
  // 2. Add corresponding AssessmentType in Organization.ts
  // 3. Create the assessment view component
  mentalHealthSubstanceAbuseScreening?: MentalHealthScreeningData;
  // Future: housingStabilityAssessment?: HousingStabilityData;

  publicEligibilityDecision?: PublicEligibilityDecision;
};

// Personal Information (Step 1: About You)
export type PersonalInformation = {
  gender: string;
  hasDisability: boolean;
  disabilities?: (keyof typeof Disability)[];
  isLgbtqia: boolean;
  citizenship: string;
  race: string[];
  isHispanic: boolean;
  isEnglishProficient: boolean;
  isVeteran: boolean;
  isMilitarySpouse?: boolean;
  maritalStatus: keyof typeof MaritalStatus;
  education: keyof typeof EducationalAttainment;
  zipcode: string;
  transportation: keyof typeof TransportationSituation;
  housing: keyof typeof HousingSituation;
};

export const ApplicationEmploymentStatus = {
  employed_full_time: "Employed Full-Time",
  employed_part_time: "Employed Part-Time",
  unemployed: "Unemployed",
  student: "Student",
  retired: "Retired",
  disability: "Disability",
  other: "Other",
} as const;

export type ApplicationEmploymentStatusKey =
  keyof typeof ApplicationEmploymentStatus;

// Financial Information (Step 2: Financial Standing)
export type FinancialInformation = {
  householdSize: number;
  dependents: number;
  employmentStatus: ApplicationEmploymentStatusKey;
  hourlyWage?: number;
  hoursPerWeek?: number;
  weeklyIncome?: number;
  totalIncomeLastSixMonths: number;
  publicAssistance: (keyof typeof SocialBenefits)[];
  hasChildSupport?: boolean;
  isChildSupportCurrent?: boolean;
};

// Background Information (Steps 3-5: Driving, Criminal, Medical)
export type BackgroundInformation = {
  driverLicense: DriverLicenseRecord;
  criminalRecord: CriminalRecord;
  medical: MedicalRecord;
};

// Driving History (Step 3)
export type DriverLicenseRecord = {
  hasActiveLicense: boolean;
  licenseState?: USState;
  hadPreviousLicense?: boolean;
  inactiveReason?: keyof typeof DriverLicenseInactivityReason;
  reinstatementCost?: number;
  hasTwoPlusLifetimeDUI?: boolean;
  hasSeriousViolationsSince2018: boolean | "i_dont_know"; // Special YesNoIDontKnow type
  specificViolations?: string[];
  wasSuspendedForPoints?: boolean;
  hasTwoOrMoreAccidentsPast7Years: boolean;
};

// Criminal History (Step 4)
export type CriminalRecord = {
  hasPendingCases?: boolean;
  isCurrentlyIncarcerated?: boolean;
  housingUnit?: string;
  facilityId?: string;
  releaseDate?: string;
  paroleHearing?: string;
  nextCourtDate?: string;
  postReleaseCity?: string;
  postReleaseState?: string;
  hasPostReleaseCarAccess?: boolean;
  postReleaseLiving?: keyof typeof HousingSituation;
  wasPreviouslyIncarcerated?: boolean;
  hasFelonyConviction?: boolean;
  hasDrugRelatedFelony?: boolean;
  drugFelonyTypes?: string[];
  hasViolentOffense?: boolean;
  hasFivePlusFeloniesPost2018?: boolean;
  hasThreePlusViolentPost2022?: boolean;
  hasVehicularHomicide?: boolean;
  hasArson?: boolean;
  hasTerrorismTreason?: boolean;
  hasKidnapping?: boolean;
  hasAssaultIntentMurder?: boolean;
  hasMurder?: boolean;
  hasCmvRelatedFelonies?: boolean;
  hasHumanTrafficking?: boolean;
  hasSexOffense?: boolean;
  totalIncarcerationYears?: number;
  lastReleaseDate?: string;
  incarcerationDuration?: keyof typeof IncarcerationDuration;
  hasPreviousArrest?: boolean;
  hasDiversionProgram?: boolean;
  hasJusticeImpactedFamily?: boolean;
  isCurrentProbationOrParole?: boolean;
  canCrossStateLines?: boolean;
  hasPreviousProbationParole?: boolean;
};

// Medical History (Step 5)
export type MedicalRecord = {
  takesMedication: boolean;
  hasFailedDOTTest?: boolean;
  hasVisionProblems?: boolean;
  hasSleepApnea?: boolean;
  hasEpilepsy?: boolean;
  isOnOxygenTherapy?: boolean;
  hasRecentCardiacEvents?: boolean;
  hasHighBloodPressure?: boolean;
  specificMedications?: string[];
  hasCurrentMarijuanaUse?: boolean;
};

// CLP Class enum
export type CLPClass = "class_a" | "class_b" | "none";

// Common non-CDL professional driving experience types
export const PriorDrivingExperienceType = {
  rideshare: "Rideshare (Uber, Lyft)",
  delivery: "Delivery Driver (Amazon, FedEx, UPS)",
  food_delivery: "Food Delivery (DoorDash, Instacart)",
  taxi: "Taxi/Limo Driver",
  bus: "Bus Driver (Non-CDL)",
  moving: "Moving Company",
  courier: "Courier/Messenger",
  personal: "Personal/Family Vehicle for Work",
  other: "Other",
} as const;

export type PriorDrivingExperienceKey = keyof typeof PriorDrivingExperienceType;

// Vocational/CDL History (Step 6)
export type VocationalHistory = {
  hasCDL?: boolean; // Has an active Commercial Driver's License
  // The following fields only apply if hasCDL is false
  hasCLP?: boolean; // Commercial Learner's Permit (non-expired)
  clpClass?: CLPClass; // Class A, Class B, or None
  clpExpirationDate?: string;
  hasDOTPhysical?: boolean; // DOT Medical Examiner's Certificate
  dotPhysicalExpirationDate?: string;
  isDotPhysicalCdlApplicantHolder?: boolean; // DOT Physical says "Yes" to CLP/CDL applicant holder
  hasPreviousCDLExperience?: boolean;
  // Only asked if hasPreviousCDLExperience is false
  hasPriorProfessionalDrivingExperience?: boolean;
  priorDrivingExperienceTypes?: PriorDrivingExperienceKey[];
};

// Form step types for wizard navigation
export type WizardStep =
  | "personal"
  | "financial"
  | "driving"
  | "criminal"
  | "medical"
  | "vocational"
  | "trade_questions"
  | "wellbeing"
  | "review";

export type StepData = {
  personal: PersonalInformation;
  financial: FinancialInformation;
  driving: DriverLicenseRecord;
  criminal: CriminalRecord;
  medical: MedicalRecord;
  vocational: VocationalHistory;
  review: Record<string, never>; // Empty - just for completion
};

// Yes/No/I Don't Know response type
export type YesNoIDontKnowResponse = boolean | "i_dont_know" | "not_sure";

// Helper function to convert "I don't know" responses to boolean for logic
export function convertIDontKnowToBoolean(
  value: YesNoIDontKnowResponse
): boolean {
  return value === true; // Only true counts as true, everything else is false
}
